// @see @/docs/flow-cart-items.md
import { environment } from '@/helpers/Environment';
import { isTimedItem } from '@/store/CartItem';
export function groupFlowDisplayItems(items, pendingRemovalCartItemId, flowDefinitions, formatExperienceCount) {
    // Non-flow carts bypass grouping, re-sorting, and image injection entirely.
    if (!items.some((i) => i.flowResolutionId))
        return items;
    // Partition: flow items grouped by resolution ID, non-flow items bucketed for final ordering.
    // Input from Cart.ts is sorted as memberships → timed (by startTime) → untimed,
    // so each bucket preserves its relative order from the input.
    const flowGroups = new Map();
    const memberships = [];
    const timed = [];
    const untimed = [];
    for (const item of items) {
        if (item.flowResolutionId) {
            const existing = flowGroups.get(item.flowResolutionId);
            if (existing)
                existing.push(item);
            else
                flowGroups.set(item.flowResolutionId, [item]);
        }
        else if (item.isMembershipEvent) {
            memberships.push(item);
        }
        else if (isTimedItem(item)) {
            timed.push(item);
        }
        else {
            untimed.push(item);
        }
    }
    // Build aggregated flow group entries with images.
    const flowGroupItems = [];
    for (const [flowId, groupItems] of flowGroups) {
        const primary = groupItems[0];
        const entry = {
            ...primary,
            id: `flow:${flowId}`,
            name: primary.flowName ?? flowId,
            image: flowImage(primary, flowDefinitions),
            faceValue: groupItems.reduce((sum, i) => sum + i.faceValue, 0),
            ticketIds: groupItems.flatMap((i) => i.ticketIds),
            isAnchor: groupItems.some((i) => i.isAnchor),
            isDependency: groupItems.some((i) => i.isDependency),
            isMembershipEvent: groupItems.some((i) => i.isMembershipEvent),
            isPendingRemoval: `flow:${flowId}` === pendingRemovalCartItemId,
            flowSummaryText: computeFlowSummaryText(groupItems, formatExperienceCount),
        };
        flowGroupItems.push(entry);
    }
    // Memberships → flow groups → timed → untimed.
    return [...memberships, ...flowGroupItems, ...timed, ...untimed];
}
export function computeFlowSummaryText(items, formatExperienceCount) {
    const datePart = flowDateRange(items);
    const countLabel = formatExperienceCount(items.length);
    return datePart ? `${datePart} - ${countLabel}` : countLabel;
}
function flowDateRange(items) {
    const timedItems = items
        .filter(isTimedItem)
        .sort((a, b) => (a.startTime.isBefore(b.startTime) ? -1 : a.startTime.isAfter(b.startTime) ? 1 : 0));
    if (timedItems.length === 0)
        return '';
    if (timedItems.length === 1)
        return timedItems[0].startTime.format('ddd D MMMM');
    const uniqueDates = [...new Set(timedItems.map((i) => i.startTime.format('YYYY-MM-DD')))];
    if (uniqueDates.length <= 1)
        return timedItems[0].startTime.format('ddd D MMMM');
    const first = timedItems[0].startTime.format('ddd D MMM');
    const latestEnd = timedItems.reduce((max, i) => (i.endTime.isAfter(max) ? i.endTime : max), timedItems[0].endTime);
    const last = latestEnd.format('ddd D MMM');
    // \u2013 = en dash, distinguishes the date range from the hyphen separator before the count.
    return `${first} \u2013 ${last}`;
}
function flowImage(item, flowDefinitions) {
    if (item.flowDefinitionId) {
        const flowDef = flowDefinitions[item.flowDefinitionId];
        if (flowDef) {
            const img = flowDefinitionImage(flowDef);
            if (img)
                return img;
        }
    }
    return { url: undefined, alt: '', noCropping: false };
}
function flowDefinitionImage(flowDef) {
    const image = flowDef?.meta?.images?.[0];
    if (!image?.asset_id)
        return null;
    const noCropping = image.no_crop ?? false;
    const sizeParams = noCropping ? 'sizex=128' : 'sizex=128&sizey=102';
    return {
        url: `/api/assets/${environment.portal.slug}/${image.asset_id}?shrinkonly&${sizeParams}`,
        alt: image.alt_text ?? `Image for ${flowDef.name}`,
        noCropping,
    };
}
